import ICustomForm, {ValidationError} from "./ICustomForm";
import m, {Vnode, VnodeDOM} from "mithril";
import ICustomFormSettings from "./ICustomFormSettings";
import ValidationBox from "./shared/components/ValidationBox/ValidationBox";
import Controls from "../ui/components/controls";
import {CellItem} from "../model/items/cellItem";
import {CheckboxItem} from "../model/items/checkbox";
import {VideoItem} from "../model/items/video";
import {ImageItem} from "../model/items/imageItem";
import Image from "./shared/components/Image/Image";

/**
 * Ancestor of custom forms
 */
export default abstract class CustomForm implements ICustomForm
{
    protected heroImage: Image|undefined;
    protected readonly controls: Controls;
    protected readonly validateBox: ValidationBox;
    protected videoItem: VideoItem|undefined;

    public constructor(
        protected settings: ICustomFormSettings
    ) {
        this.controls = new Controls(settings, true);
        this.validateBox = new ValidationBox(settings.form);
        this.videoItem = <VideoItem|undefined>this.settings.form.grid?.cellItems.filter(c => c instanceof VideoItem)[0];

        const imageItem = <ImageItem|undefined>this.settings.form.grid?.cellItems.filter(c => c instanceof ImageItem)[0];
        if (imageItem) {
            this.heroImage = new Image(imageItem);
        }
    }

    public onValidationErrors(errors: ValidationError[]): void {
        // override me when you need
    }

    public abstract view(): Vnode;

    public rewriteFormControls() {
        return false;
    }

    checkRequirements(): boolean {
        return true;
    }

    // HELPERS
    /**
     * Check if variable belongs to form
     */
    private hasVariable(variableName: string): boolean {
        return this.settings.form.variables.hasOwnProperty(variableName);
    }

    /**
     * Get variable value by its name.
     * If name variable does not exists (or not belongs to form), got undefined
     */
    protected getVariable(variableName: string): any {
        if (this.hasVariable(variableName)) {
            return this.settings.form.questionary.state.variables[variableName]();
        }
        return undefined;
    }

    /**
     * Set variable value
     * If variable does not exists (or not belongs to form), throw exception
     */
    protected setVariable(variableName: string, value: any): void {
        if (this.hasVariable(variableName)) {
            this.settings.form.questionary.state.variables[variableName](value);
        } else {
            throw Error(`Cannot set variable. Variable '${variableName}' does not exist on form ${this.settings.form.id}.`)
        }
    }

    /**
     * Create controls block
     */
    protected viewControls(nextButtonText = 'nextButton'): Vnode|null {
        this.controls.nextButtonText = nextButtonText;
        return m(this.controls);
    }

    /**
     * View of heroImage
     */
    protected viewHeroImage(): Vnode|null {
        if (this.heroImage) {
            return m('.hero-image', {}, [
                m(this.heroImage)
            ]);
        }
        return null;
    }

    protected viewHeroVideo() {
        if (this.videoItem) {
            return m('.hero-video', {}, [
                this.videoItem.getView()
            ]);
        }
        return null;
    }

    /**
     * @param content Expect content of custom form
     * @param controls Optional, rewrite normal controls (ex.: in case of unusual behavior)
     */
    protected viewLayout(content: Vnode|(Vnode|null)[]|null, controls?: Vnode|(Vnode|null)[]|null) {

        if (Array.isArray(content)) {
            content.push(m(this.validateBox))
        } else {
            content = [content, m(this.validateBox)];
        }

        return m(".customForm", {}, [
            m(this.settings.app.header),
            m("div", {
                "class": this.settings.formHtmlClass(),
            }, [
                m(".content", {}, content),
                controls === undefined ? this.viewControls() : controls,
            ]),

        ]);
    }

    //------------------//
    // HANDLERS
    //
    protected onSetValue(cellItem: CellItem, value: any): void {

        if (cellItem instanceof CheckboxItem) { // local uknown
            cellItem.set();
            return;
        }

        this.setVariable(cellItem.variableName, value);
        const applyGlobalUnknown = value && cellItem instanceof CheckboxItem && cellItem.globalUnknown;

        for (const cell of this.settings.form.grid.cells) {
            for (const item of cell.cellItems) {

                if (applyGlobalUnknown && item !== cellItem) {
                    item.reset();
                } else if (item !== cellItem && item instanceof CheckboxItem && item.globalUnknown) {
                    item.setVariable(0);
                }
            }
        }
    }

    oncreate(vnode: VnodeDOM) {

    }
}
